import { typedObjectEntries } from '#/types/utils';
import type { Svg } from '#/types/svg';

import AerialTramIcon from '#/svg-assets/agencies/AERIALTRAM.svg';
import ClackamasIcon from '#/svg-assets/agencies/CLACKAMAS.svg';
import CTRANIcon from '#/svg-assets/agencies/CTRAN.svg';
import CTRANFlexIcon from '#/svg-assets/agencies/CTRANCURRENT.svg';
import PSCIcon from '#/svg-assets/agencies/PSC.svg';
import SAMIcon from '#/svg-assets/agencies/SAM.svg';
import SMARTIcon from '#/svg-assets/agencies/SMART.svg';
import MultCoIcon from '#/svg-assets/agencies/SWAN.svg';
import TriMetIcon from '#/svg-assets/agencies/TRIMET.svg';
import WashingtonParkIcon from '#/svg-assets/agencies/WAPARK.svg';
import WashingtonFlexIcon from '#/svg-assets/agencies/SPOT.svg';
import RideConnectionIcon from '#/svg-assets/agencies/RIDECONNECTION.svg';

export const ENGLISH_LOCALE_CODE = 'en';
export const CHINESE_LOCALE_CODE = 'zh';
export const MAX_SHORT_NAME = 'MAX';
export const FX_SHORT_NAME = 'FX2';
export const AERIAL_TRAM_SHORT_NAME = 'AT';

// TODO: PRJ77-1391 - Go throughout application, find instances of 'TRIMET' and use FEEDS.TRIMET instead
export const FEEDS = {
  CLACKAMAS: 'CLACKAMAS',
  TRIMET: 'TRIMET',
  RIDECONNECTION: 'RIDECONNECTION',
  SAM: 'SAM',
  SMART: 'SMART',
  MULT: 'MULT',
  WAPARK: 'WAPARK',
  CTRAN: 'CTRAN',
  CTRAN_FLEX: 'CTRAN_FLEX',
  WASH_FLEX: 'WASH_FLEX',
} as const;

export type FeedId = (typeof FEEDS)[keyof typeof FEEDS];
export const isFeedId = (v: string | null | undefined): v is FeedId => {
  const feedIds: readonly string[] = Object.values(FEEDS);
  return !!v && feedIds.includes(v);
};

export const TRIMET_GTFS_AGENCY_IDS = {
  TriMet: `${FEEDS.TRIMET}:TRIMET`,
  Streetcar: `${FEEDS.TRIMET}:PSC`,
  AerialTram: `${FEEDS.TRIMET}:TRAM`,
};

const baseLanguages = [
  {
    nameIntlIdSuffix: 'english-name',
    displayName: 'English',
    symbol: ENGLISH_LOCALE_CODE,
    smartlingTranslationAvailable: true,
  },
  {
    nameIntlIdSuffix: 'spanish-name',
    displayName: 'Español',
    symbol: 'es',
    smartlingTranslationAvailable: true,
  },
  {
    nameIntlIdSuffix: 'vietnamese-name',
    displayName: 'TiếngViệt',
    symbol: 'vi',
    smartlingTranslationAvailable: true,
  },
  {
    nameIntlIdSuffix: 'korean-name',
    displayName: '한국어',
    symbol: 'ko',
    smartlingTranslationAvailable: true,
  },
  {
    nameIntlIdSuffix: 'russian-name',
    displayName: 'РУССКИЙ',
    symbol: 'ru',
    smartlingTranslationAvailable: true,
  },
  {
    nameIntlIdSuffix: 'chinese-name',
    displayName: '中文',
    symbol: CHINESE_LOCALE_CODE,
    smartlingTranslationAvailable: true,
  },
  {
    nameIntlIdSuffix: 'ukrainian-name',
    displayName: 'Українськи',
    symbol: 'uk',
    smartlingTranslationAvailable: false,
  },
  {
    nameIntlIdSuffix: 'japanese-name',
    displayName: '日本語',
    symbol: 'ja',
    smartlingTranslationAvailable: false,
  },
  {
    nameIntlIdSuffix: 'romanian-name',
    displayName: 'Română',
    symbol: 'ro',
    smartlingTranslationAvailable: false,
  },
  {
    nameIntlIdSuffix: 'tagalog-name',
    displayName: 'Tagalog',
    symbol: 'tl',
    smartlingTranslationAvailable: false,
  },
  {
    nameIntlIdSuffix: 'khmer-name',
    displayName: 'ឈ្មោះភាសា',
    symbol: 'km',
    smartlingTranslationAvailable: false,
  },
  {
    nameIntlIdSuffix: 'somalian-name',
    displayName: 'Somali',
    symbol: 'so',
    smartlingTranslationAvailable: false,
  },
  {
    nameIntlIdSuffix: 'arabic-name',
    displayName: 'اللغة العربية',
    symbol: 'ar',
    smartlingTranslationAvailable: false,
  },
  {
    nameIntlIdSuffix: 'farsi-name',
    displayName: 'فارسی',
    symbol: 'fa',
    smartlingTranslationAvailable: false,
  },
] as const;

// The mapping is separate from the export and type declaration here
// to ensure the exported constant cannot accidentally be modified
// see: https://www.reddit.com/r/typescript/comments/u7qcdv/comment/i5gbwk7/
const mappedBaseLanguages = baseLanguages.map(lang => ({
  ...lang,
  nameIntlId: `language-picker.${lang.nameIntlIdSuffix}`,
}));

type LanguageOption = (typeof mappedBaseLanguages)[number];

export const LANGUAGE_OPTIONS: readonly LanguageOption[] = mappedBaseLanguages;

const supportedLangSymbols = LANGUAGE_OPTIONS.map(language => {
  return language.symbol;
});

export type LanguageSymbols = (typeof supportedLangSymbols)[number];

const baseLanguageLookup = LANGUAGE_OPTIONS.reduce(
  (lookup, item) => ({
    ...lookup,
    [item.symbol]: item,
  }),
  {}
);

export const LANGUAGE_LOOKUP: Readonly<
  Record<LanguageSymbols, LanguageOption> | Record<string, never>
> = baseLanguageLookup;

export const SUPPORTED_LOCALES: readonly LanguageSymbols[] =
  supportedLangSymbols;

export const isLanguageSymbol = (value: unknown): value is LanguageSymbols =>
  typeof value === 'string' &&
  (SUPPORTED_LOCALES as readonly string[]).includes(value);

export const SERVICE_STATE = 'Oregon';

// States included in the regional service area
export const VALID_SERVICE_STATES = [SERVICE_STATE, 'Washington'];

export const DEFAULT_FONT_FAMILY =
  'Source Sans Pro, Helvetica, Arial, sans-serif';

// Route config constants
export const DEFAULT_ROUTE_COLOR = '#084c8d';
export const MAX_SHUTTLE_COLOR = '#fff';

// These stand-alone constants are being refactored away. New references to the
// feed IDs should come from the FEEDS object instead.
// Feed ID constants
export const TRIMET_FEED_ID = 'TRIMET';
export const CTRAN_FEED_ID = 'CTRAN';
export const CTRAN_FLEX_FEED_ID = 'CTRAN_FLEX';
export const SMART_FEED_ID = 'SMART';
export const SAM_FEED_ID = 'SAM';
export const CLACKAMAS_FEED_ID = 'CLACKAMAS';
export const MULT_FEED_ID = 'MULT';
export const WAPARK_FEED_ID = 'WAPARK';

// Agency ID constants (for sub-agencies)
export const TRIMET_PSC_AGENCY_ID = 'TRIMET:PSC';
export const TRIMET_TRAM_AGENCY_ID = 'TRIMET:TRAM';

// Pure agency identifiers (without feed prefix)
export const PSC_AGENCY_ID = 'PSC';
export const TRAM_AGENCY_ID = 'TRAM';

// CTRAN Vine route IDs (Red and Blue Vine lines)
export const CTRAN_VINE_ROUTE_IDS = ['050', '051'] as const;

// Maximum width of the content
export const MAX_CONTENT_WIDTH_NUMBER = 1920;
export const MAX_CONTENT_WIDTH = `${MAX_CONTENT_WIDTH_NUMBER}px`;

// Very small viewport width, usually indicating the user has zoomed in on a mobile device
const XXXSZOOM_BREAK_NUMBER = 250;
export const XXXSZOOM_BREAK = `${XXXSZOOM_BREAK_NUMBER}px`;

// Extra extra small devices: Usually older mobile devices
const XXSPHONE_BREAK_NUMBER = 380;
export const XXSPHONE_BREAK = `${XXSPHONE_BREAK_NUMBER}px`;

// Extra small devices: Smaller mobile devices
const XSPHONE_BREAK_NUMBER = 480;
export const XSPHONE_BREAK = `${XSPHONE_BREAK_NUMBER}px`;

// Small devices: larger mobile, small tablets
const PHONE_BREAK_NUMBER = 576;
export const PHONE_BREAK = `${PHONE_BREAK_NUMBER}px`;

// Medium devices: Most tablets portrait, smaller desktops
const TABLET_BREAK_NUMBER = 768;
export const TABLET_BREAK = `${TABLET_BREAK_NUMBER}px`;

// Large devices: Desktops, tablet landscapes, ...
export const DESKTOP_MIN_BREAK_NUMBER = 992;
export const DESKTOP_MIN_BREAK = `${DESKTOP_MIN_BREAK_NUMBER}px`;

// Catch all mobile devices MAX breakpoint
export const MOBILE_MAX_BREAK_NUMBER = DESKTOP_MIN_BREAK_NUMBER - 1;
export const MOBILE_MAX_BREAK = `${MOBILE_MAX_BREAK_NUMBER}px`;

const XLDESKTOP_BREAK_NUMBER = 1200;
export const XLDESKTOP_BREAK = `${XLDESKTOP_BREAK_NUMBER}px`;

export const IS_TOUCH_DEVICE =
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(pointer: coarse)').matches;

export type MinWidthBreakpoint =
  | typeof XXSPHONE_BREAK
  | typeof XSPHONE_BREAK
  | typeof PHONE_BREAK
  | typeof TABLET_BREAK
  | typeof DESKTOP_MIN_BREAK
  | typeof XLDESKTOP_BREAK;

// Side panel is displayed at DESKTOP_MIN_BREAK
export const SIDE_PANEL_MIN_WIDTH_NUMBER = 390;
export const SIDE_PANEL_MIN_WIDTH = `${SIDE_PANEL_MIN_WIDTH_NUMBER}px`;
export const SIDE_PANEL_MAX_WIDTH_NUMBER = 500;
export const SIDE_PANEL_MAX_WIDTH = `${SIDE_PANEL_MAX_WIDTH_NUMBER}px`;
export const SIDE_PANEL_OFFSET_DECIMAL = 0.02;
export const SIDE_PANEL_OFFSET = `${SIDE_PANEL_OFFSET_DECIMAL * 100}%`;
export const SIDE_PANEL_WIDTH_DECIMAL = 0.36;
export const SIDE_PANEL_WIDTH = `${SIDE_PANEL_WIDTH_DECIMAL * 100}%`;
export const SIDE_PANEL_DURATION_VALUE = 300;
export const SIDE_PANEL_DURATION = `${SIDE_PANEL_DURATION_VALUE}ms`;
export const SIDE_PANEL_PADDING_BOOST_DECIMAL = 0.05;

// standard side of a checkbox or radio button input
export const CHECKBOX_RADIO_HEIGHT_WIDTH = '22px';

export const RECENT_SEARCH_RESULTS_LIMIT = 10;
export const GOV_DELIVERY_ROUTE =
  'https://public.govdelivery.com/accounts/ORTRIMET/subscribers/qualify';

export const TIME_SELECTOR = 'time';
export const DATE_SELECTOR = 'date';
export const DEPARTURE_OPTION_SELECTOR = 'departArrive';
export const ARRIVE_BY_SELECTOR = 'arriveBy';
export const MODE_SELECTOR = 'mode';
export const DEPART_AT_OPTION = 'DEPART';
export const ARRIVE_BY_OPTION = 'ARRIVE';
export const LEAVE_NOW_OPTION = 'LEAVE_NOW';

// TODO (PUBWEB-2529): Phase out usage of these constants. Use the values
//  from either `OTP_ENGINE_LOCATION_TYPES` and `OTP_UI_LOCATION_TYPES` instead,
//  depending on the context of your use case
export const OTP2_ORIGIN_LOCATION_TYPE = 'fromPlace';
export const OTP2_DESTINATION_LOCATION_TYPE = 'toPlace';
export const ORIGIN_LOCATION_TYPE = 'from';
export const DESTINATION_LOCATION_TYPE = 'to';

// The values in this object are keys used to pass origin and destination
// to the OTP engine to generating a trip
export const OTP_ENGINE_LOCATION_TYPES = {
  origin: OTP2_ORIGIN_LOCATION_TYPE,
  destination: OTP2_DESTINATION_LOCATION_TYPE,
} as const;

export type OtpEngineLocationType =
  (typeof OTP_ENGINE_LOCATION_TYPES)[keyof typeof OTP_ENGINE_LOCATION_TYPES];

// The values in this object are location types accepted as prop values
// by otp-ui components, affecting how those components are rendered
export const OTP_UI_LOCATION_TYPES = {
  origin: ORIGIN_LOCATION_TYPE,
  destination: DESTINATION_LOCATION_TYPE,
} as const;

export type OtpUiLocationType =
  (typeof OTP_UI_LOCATION_TYPES)[keyof typeof OTP_UI_LOCATION_TYPES];

export const otpUiToEngineLocationTypeMap = {
  [OTP_UI_LOCATION_TYPES.origin]: OTP2_ORIGIN_LOCATION_TYPE,
  [OTP_UI_LOCATION_TYPES.destination]: OTP2_DESTINATION_LOCATION_TYPE,
} as const satisfies Record<OtpUiLocationType, OtpEngineLocationType>;

export const CURRENT_LOCATION_TYPE = 'current';

// Types describing how a trip origin/destination was entered by the user.
// Used for analytics via GraphQL request extensions.
export const PLACE_INPUT_TYPES = {
  GPS: 'GPS',
  STOP: 'STOP',
  PARK_AND_RIDE: 'PR',
  ADDRESS: 'ADDRESS',
  MAP: 'MAP',
  URL: 'URL',
} as const;

export type PlaceInputType =
  (typeof PLACE_INPUT_TYPES)[keyof typeof PLACE_INPUT_TYPES];

// storage keys
export const THEME_STORAGE_KEY = 'theme';
export const PRINT_TRIP_STORAGE_KEY = 'printTripData';
export const MANUAL_LOCATION_STORAGE_KEY = 'manualLocation';
export const GEOLOCATION_APPROVED_STORAGE_KEY = 'geolocationApproved';
export const RECENT_SEARCHES_STORAGE_KEY = 'omnibox:recentsearches';
export const BASE_LAYER_STORAGE_KEY = 'baseLayer';
export const IS_3D_MODE_STORAGE_KEY = 'is3DMode';
export const CHECK_STORAGE_KEY = 'storageCheck';
export const ALERT_TIMESTAMP_KEY = 'alertTimestamp';
export const ALERT_DISMISSED_KEY = 'alertDismissed';
export const SELECTED_LOCALE_STORAGE_KEY = 'selectedLocale';
export const DISMISSED_ITEMS_STORAGE_KEY = 'dismissedItems';

export const LOCALE_QUERY_STRING_KEY = 'locale';

export const OTP_UI_CURRENT_LOCATION_TYPE = 'CURRENT_LOCATION';

export const Z_INDEX_499 = 499;
export const Z_INDEX_500 = 500;
export const Z_INDEX_600 = 600;
export const Z_INDEX_1000 = 1000;
export const Z_INDEX_TOP = 9999;
export const Z_INDEX_SECOND_TOP = Z_INDEX_TOP - 1;
export const Z_INDEX_THIRD_TOP = Z_INDEX_SECOND_TOP - 1;
export const NAV_HEIGHT_VALUE = 50;
export const NAV_HEIGHT = `${NAV_HEIGHT_VALUE}px`;
export const NAV_PADDING_TOP_VALUE = 15;
export const NAV_PADDING_TOP = `${NAV_PADDING_TOP_VALUE}px`;
export const NAV_OFFSET_DETAIL_VIEW = `${
  NAV_PADDING_TOP_VALUE + NAV_HEIGHT_VALUE
}px`;
export const DETAIL_HEADER_HEIGHT = `${NAV_HEIGHT_VALUE}px`;
export const SYSTEM_ALERT_BANNER_HEIGHT_VALUE = 100;
export const SYSTEM_ALERT_BANNER_HEIGHT = `${SYSTEM_ALERT_BANNER_HEIGHT_VALUE}px`;
export const SYSTEM_PLUS_NAV_VALUE =
  SYSTEM_ALERT_BANNER_HEIGHT_VALUE + NAV_HEIGHT_VALUE;
export const SYSTEM_PLUS_NAV = `${SYSTEM_PLUS_NAV_VALUE}px`;
export const DETAILNAV_BUTTON_VALUE = 32;
export const DETAILNAV_BUTTON_WIDTH = `${DETAILNAV_BUTTON_VALUE}px`;
export const DETAILNAV_BUTTON_HEIGHT = `${DETAILNAV_BUTTON_VALUE}px`;

export const LOCATION_ICON_WIDTH = 30;
export const DEFAULT_INPUT_HEIGHT_VALUE = 55;
export const DEFAULT_INPUT_HEIGHT = `${DEFAULT_INPUT_HEIGHT_VALUE}px`;

export const AERIAL_TRAM_MODE = 'GONDOLA';
export const BICYCLE_MODE = 'BICYCLE';
export const BICYCLE_RENT_MODE = 'BICYCLE_RENT';
export const BICYCLE_PARK_MODE = 'BICYCLE_PARK';
export const BUS_MODE = 'BUS';
export const BUS_FX_MODE = 'BUS_FX';
export const CAR_MODE = 'CAR';
export const CAR_HAIL_MODE = 'CAR_HAIL';
export const CAR_RENT_MODE = 'CAR_RENT';
export const CAR_DROP_OFF_MODE = 'CAR_DROP_OFF';
export const FERRY_MODE = 'FERRY';
export const LIGHT_RAIL_MODE = 'TRAM';
export const MAIN_TRANSIT_MODE = 'TRANSIT';
export const MICROMOBILITY_MODE = 'MICROMOBILITY';
export const MICROMOBILITY_RENT_MODE = 'MICROMOBILITY_RENT';
export const PARK_AND_RIDE_MODE = 'CAR_PARK';
export const RAIL_MODE = 'RAIL';
export const SUBWAY_MODE = 'SUBWAY';
export const WALK_MODE = 'WALK';
export const OTP2_SCOOTER_MODE = 'SCOOTER';

export const BIKE_SHARE_VERTEX_TYPE = 'BIKESHARE';
export const SCOOTER_CATEGORY_ID = 'SCOOTER';

// alternate company name for BIKETOWN, since Lyft is
// operating BIKETOWN until at least 2028,
// this name may change after that time
export const OTP2_BIKETOWN_NAME = 'lyft_pdx';

// Aerial Tram code
export const AERIAL_TRAM_ROUTE_ID = 208;
export const AERIAL_TRAM_ROUTE_ID_LABEL = `${FEEDS.TRIMET}:${AERIAL_TRAM_ROUTE_ID}`;

// FX Bus Code
export const FX_BUS_ROUTE_ID = 2;

// WES Train Code
export const WES_TRAIN_ROUTE_ID = 203;

// train codes
export const TRAIN_ALL_CODES = [
  90, 100, 190, 200, 290, 203, 193, 194, 195,
] as const;

export const MAX_ROUTE_IDS = {
  BLUE: 100,
  RED: 90,
  GREEN: 200,
  ORANGE: 290,
  YELLOW: 190,
};

export const MAX_TRAIN_CODES = ['290', '200', '190', '90', '100'] as const;
export const STREETCAR_ROUTE_IDS: readonly string[] = ['193', '194', '195'];
export const STREETCAR_GTFS_ROUTE_IDS = STREETCAR_ROUTE_IDS.map(
  routeId => `${FEEDS.TRIMET}:${routeId}`
);

export const STREETCAR_GOV_DELIVERY_TOPIC = '193';

// the following are not distinct modes within OpenTripPlanner, but are
// needed within this application
export const OTP2_SCOOTER_VERTEX_TYPE = 'BIKESHARE';
export const PARATRANSIT_MODE = 'LIFT';
export const STREETCAR_MODE = 'STREETCAR';

export const MICROMOBILITY_STARTING_TYPE = 'VEHICLERENTAL';
export const STREETCAR_GTFS_AGENCY_ID = 'TRIMET:PSC';

export const GTM_DATA_ATTRIBUTE = 'data-gtm';
export const STORAGE_PREFIX = 'trimet';

// Route provider configuration for filtering
export interface RouteProviderConfig {
  feedId: string;
  agencyId: string | null;
  name: string;
  displayName: string; // Short name for UI display (e.g., filters, labels)
  Icon: Svg;
}

export const ROUTE_PROVIDER_CONFIG: RouteProviderConfig[] = [
  {
    feedId: FEEDS.TRIMET,
    agencyId: null,
    name: 'TriMet',
    displayName: 'TriMet',
    Icon: TriMetIcon,
  },
  {
    feedId: FEEDS.CTRAN,
    agencyId: null,
    name: 'C-TRAN',
    displayName: 'C-TRAN',
    Icon: CTRANIcon,
  },
  {
    feedId: FEEDS.CTRAN_FLEX,
    agencyId: null,
    name: 'The Current by C-TRAN',
    displayName: 'The Current by C-TRAN',
    Icon: CTRANFlexIcon,
  },
  {
    feedId: FEEDS.TRIMET, // Streetcar uses TRIMET feedId but PSC agency
    agencyId: TRIMET_PSC_AGENCY_ID,
    name: 'Portland Streetcar',
    displayName: 'Portland Streetcar',
    Icon: PSCIcon,
  },
  {
    feedId: FEEDS.TRIMET, // Aerial Tram uses TRIMET feedId but TRAM agency
    agencyId: TRIMET_TRAM_AGENCY_ID,
    name: 'Portland Aerial Tram',
    displayName: 'Aerial Tram',
    Icon: AerialTramIcon,
  },
  {
    feedId: FEEDS.SMART,
    agencyId: null,
    name: 'SMART',
    displayName: 'SMART',
    Icon: SMARTIcon,
  },
  {
    feedId: FEEDS.SAM,
    agencyId: null,
    name: 'Sandy Area Metro',
    displayName: 'Sandy Area Metro',
    Icon: SAMIcon,
  },
  {
    feedId: FEEDS.CLACKAMAS,
    agencyId: null,
    name: 'Clackamas County Social Services',
    displayName: 'Clackamas County',
    Icon: ClackamasIcon,
  },
  {
    feedId: FEEDS.MULT,
    agencyId: null,
    name: 'Multnomah County Transit',
    displayName: 'Multnomah County',
    Icon: MultCoIcon,
  },
  {
    feedId: FEEDS.WAPARK,
    agencyId: null,
    name: 'Washington Park Shuttle',
    displayName: 'Washington Park',
    Icon: WashingtonParkIcon,
  },
  {
    feedId: FEEDS.WASH_FLEX,
    agencyId: null,
    name: 'SPOT Southwest',
    displayName: 'SPOT',
    Icon: WashingtonFlexIcon,
  },
  {
    feedId: FEEDS.RIDECONNECTION,
    agencyId: null,
    name: 'Ride Connection',
    displayName: 'Ride Connection',
    Icon: RideConnectionIcon,
  },
];

export const AERIAL_TRAM_LINK = 'https://www.gobytram.com';
export const SECURITY_LINK =
  'https://support.trimet.org/hc/en-us/requests/new?ticket_form_id=18816337246235';
export const HOP_HOME_LINK = 'https://myhopcard.com/home/';

export const STREETCAR_LABEL = 'Streetcar';
export const AERIAL_TRAM_LABEL = 'Aerial Tram';
export const MAX_LIGHT_RAIL_LABEL = 'MAX Light Rail';
export const WES_LABEL = 'WES';
export const WES_COMMUTER_RAIL_LABEL = 'WES Commuter Rail';
export const PLURAL_BUS_LABEL = 'Buses';
export const TRIMET_FX_LABEL = 'FX';
export const CTRAN_THE_CURRENT_LABEL = 'The Current';
export const PLURAL_SHUTTLE_LABEL = 'Shuttles';
export const FREE_SHUTTLE_LABEL = 'Free Shuttle';
export const CLACKCO_CONNECTS_LABEL = 'ClackCo Connects';
export const TRIMET_SERVICE_NAME_LABEL = 'TriMet';
export const PORTLAND_LABEL = 'Portland';
export const CTRAN_VINE_LABEL = 'Vine';
export const CTRAN_THE_VINE_LABEL = 'The Vine';

export const COOKIES_HELP_LINK =
  'https://www.wikihow.com/Enable-Cookies-in-Your-Internet-Web-Browser#';

export const CACHE_DURATION = {
  ONE_HOUR: 3600,
  ONE_MINUTE: 60,
  FIVE_SECONDS: 5,
  TEN_SECONDS: 10,
};

export const ARRIVALS_REFRESH_INTERVAL = 5000;
export const GEOLOCATION_REPOLL_INTERVAL = 15000;
export const FEET_IN_A_MILE = 5280;

export const TRIMET_TIME_ZONE = 'America/Los_Angeles';
export const TRIMET_TIME_FORMAT = 'h:mmaaa';
export const TRIMET_DATE_FORMAT = 'MMM dd, yyyy';
export const TRIMET_ALERT_DATE_FORMAT = 'MMM d, yyyy';
export const TRIMET_MONTH_DAY_FORMAT = 'MMMM d';

// OTP2 Api does not contain maxWalkDistance so this is the default distance for
// the "walk" mode in meters.
export const DEFAULT_WALK_DISTANCE = 1609;

// Fare calculation constants
// These numeric codes are standardized by GTFS specification
export const DEFAULT_MEDIUM_ID = '2'; // Hop Fastpass
export const VIRTUAL_HOP_MEDIUM_ID = '4'; // Virtual Hop Fastpass
export const OPEN_PAYMENT_MEDIUM_ID = '3'; // Open Payment (contactless cards)
export const TICKET_MEDIUM_ID = '1'; // Ticket
export const CASH_MEDIUM_ID = '0'; // Cash
// Medium IDs to check for fares, in order of preference
// This allows regional agencies like SAM (cash only) to display fares
export const FARE_MEDIUM_IDS = [
  DEFAULT_MEDIUM_ID, // Hop Fastpass (2)
  VIRTUAL_HOP_MEDIUM_ID, // Virtual Hop Fastpass (4)
  OPEN_PAYMENT_MEDIUM_ID, // Open Payment (3)
  TICKET_MEDIUM_ID, // Ticket (1)
  CASH_MEDIUM_ID, // Cash (0)
  null, // Generic/fallback
];

// Rider category constants
export const RIDER_CATEGORY_INFO = {
  ADULT: {
    id: 'ADULT',
    i18nKey: 'fare.rider-categories.adult',
  },
  YOUTH: {
    id: 'YOUTH',
    i18nKey: 'fare.rider-categories.youth',
  },
  HONORED_CITIZEN: {
    id: 'HONORED_CITIZEN',
    i18nKey: 'fare.rider-categories.honored-citizen',
  },
  DISCOUNTED: {
    id: 'DISCOUNTED',
    i18nKey: 'fare.rider-categories.discounted',
  },
} as const;

export const RIDER_CATEGORIES = Object.values(RIDER_CATEGORY_INFO).map(
  info => info.id
);
export type RiderCategory = (typeof RIDER_CATEGORIES)[number];

/**
 * Conflation table for the fare selector dropdown.
 *
 * Maps a single display rider category (the row shown in the fare dropdown)
 * to one or more raw GTFS `rider_category_id`s that should contribute to its
 * total cost. This allows different feeds that use different rider category
 * ids (e.g. C-TRAN's `DISCOUNTED`) to be folded into the appropriate display
 * category (`HONORED_CITIZEN`).
 *
 * Order of `riderCategoryIds` is the priority order used per leg when looking
 * up a fare product. The first id with a matching fare on a given leg wins.
 *
 * If a leg has none of the listed rider categories, the fare util falls back
 * to the leg's "default" rider category (preferring ADULT) so the leg does
 * not erroneously display as free. This mirrors GTFS
 * `rider_categories.default_fare_category` semantics where possible, since
 * OTP does not currently surface that field.
 *
 * Keep this table in sync with the source of truth maintained alongside the
 * monitoring script in PRJ77-1776:
 *   https://trimet.atlassian.net/wiki/spaces/PRJ77/pages/2095579142/Fares+Rider+Category+Conflation+Table
 */
export const RIDER_CATEGORY_CONFLATION = [
  {
    id: RIDER_CATEGORY_INFO.ADULT.id,
    i18nKey: RIDER_CATEGORY_INFO.ADULT.i18nKey,
    riderCategoryIds: [RIDER_CATEGORY_INFO.ADULT.id],
  },
  {
    id: RIDER_CATEGORY_INFO.HONORED_CITIZEN.id,
    // Dropdown label for conflated HONORED_CITIZEN + DISCOUNTED (wiki display name)
    i18nKey: 'fare.rider-categories.honored-citizen-discounted',
    riderCategoryIds: [
      RIDER_CATEGORY_INFO.HONORED_CITIZEN.id,
      RIDER_CATEGORY_INFO.DISCOUNTED.id,
    ],
  },
  {
    id: RIDER_CATEGORY_INFO.YOUTH.id,
    i18nKey: RIDER_CATEGORY_INFO.YOUTH.i18nKey,
    riderCategoryIds: [RIDER_CATEGORY_INFO.YOUTH.id],
  },
] as const;

export type RiderCategoryConflationEntry =
  (typeof RIDER_CATEGORY_CONFLATION)[number];

// these are environment values set to `process.env.NODE_ENV` by Gatsby:
// https://www.gatsbyjs.com/docs/how-to/local-development/environment-variables
export const NODE_ENVS = {
  DEV: 'development',
  PROD: 'production',
} as const;

/**
 * Defines hostnames for each application environment.
 *
 * Each key represents an environment and maps to an array of hostnames
 * associated with that environment. The first hostname in each array is
 * the primary.
 *
 * The `prod` environment includes an alternative hostname to allow testing of
 * production behavior before a public release.
 */
export const APP_HOSTNAMES = {
  prod: ['trimet.org', 'st.trimet.org'],
  beta: ['beta.trimet.org'],
  labs: ['labs.trimet.org'],
  dev: ['dv.trimet.org'],
  local: ['localhost'],
} as const satisfies Record<string, readonly string[]>;

export type AppEnv = keyof typeof APP_HOSTNAMES;

/** Mapping of hostnames to their corresponding application environment */
export const ENV_LOOKUP: Readonly<Record<string, AppEnv>> = Object.fromEntries(
  typedObjectEntries(APP_HOSTNAMES).flatMap(([env, hosts]) =>
    hosts.map((hostname): [typeof hostname, AppEnv] => [hostname, env])
  )
);

/* For gatsby browser and focus management */
export const DETAIL_NAV_HEADER_PAGE_TITLE_ID = 'detail-nav-header-page-title';
export const TRIP_TOOLS_TABS_ID = 'trip-tool-tabs';
export const DETAIL_NAV_TOP_ID = 'detail-nav-top';
export const SIDE_PANEL_ID = 'side-panel';
